import { useCallback, useState } from "react"
import { cn } from "@/lib/utils"
import { Button } from "../Button/Button"

export interface TradesPaginationProps {
  page: number
  pageSize: number
  pageCount: number
  totalRows: number
  pageSizeOptions?: number[]
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
  className?: string
}

const DEFAULT_PAGE_SIZES = [10, 15, 20, 25]

export function TradesPagination({
  page,
  pageSize,
  pageCount,
  totalRows,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
  onPageChange,
  onPageSizeChange,
  className,
}: TradesPaginationProps) {
  const rangeStart = Math.min(page * pageSize + 1, totalRows)
  const rangeEnd = Math.min((page + 1) * pageSize, totalRows)

  return (
    <div
      className={cn(
        "flex items-center justify-between px-3 border-t border-border h-11",
        className,
      )}
    >
      <div className="flex items-center gap-2">
        <span className="text-caption text-muted-foreground">
          {rangeStart}–{rangeEnd} of {totalRows}
        </span>
        <div className="hidden md:flex items-center gap-0.5 bg-muted rounded p-0.5">
          {pageSizeOptions.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => {
                onPageSizeChange(n)
                onPageChange(0)
              }}
              className={cn(
                "px-2 py-0.5 text-caption rounded transition-colors cursor-pointer",
                pageSize === n
                  ? "bg-card text-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {n}
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          disabled={page === 0}
          onClick={() => onPageChange(Math.max(0, page - 1))}
          className="text-label"
        >
          Prev
        </Button>
        <span className="text-caption text-muted-foreground">
          {page + 1} / {pageCount}
        </span>
        <Button
          variant="ghost"
          size="sm"
          disabled={page >= pageCount - 1}
          onClick={() => onPageChange(Math.min(pageCount - 1, page + 1))}
          className="text-label"
        >
          Next
        </Button>
      </div>
    </div>
  )
}

export interface PaginationControllerState {
  page: number
  pageSize: number
  pageCount: (total: number) => number
  setPage: (p: number) => void
  setPageSize: (s: number) => void
}

export function usePaginationState(
  initialPageSize: number = 15,
): PaginationControllerState {
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(initialPageSize)
  const pageCount = useCallback(
    (total: number) => Math.max(1, Math.ceil(total / pageSize)),
    [pageSize],
  )
  return { page, pageSize, pageCount, setPage, setPageSize }
}
